class PnmProduct extends HTMLElement {
  connectedCallback() {
    const json = this.querySelector('[data-pnm-product-json]');
    this.product = json ? JSON.parse(json.textContent) : null;
    if (!this.product) return;

    this.idInput = this.querySelector('[data-pnm-variant-id]');
    this.planInput = this.querySelector('[data-pnm-selling-plan]');
    this.priceEl = this.querySelector('[data-pnm-price]');
    this.compareEl = this.querySelector('[data-pnm-compare]');
    this.atc = this.querySelector('[data-pnm-atc]');
    this.planSelect = this.querySelector('[data-pnm-plan]');
    this.subWas = this.querySelector('[data-pnm-sub-was]');
    this.subNow = this.querySelector('[data-pnm-sub-now]');
    this.oncePrice = this.querySelector('[data-pnm-once-price]');

    this.limitEnabled = this.dataset.limitEnabled === 'true';
    this.limitMax = parseInt(this.dataset.limitMax, 10) || 10;
    this.limitSelector = this.buildLimitSelector(this.dataset.limitClass);
    this.limitExceeded = false;
    this.limitAria = false;
    this.limitQueued = false;

    this.variant =
      this.product.variants.find((v) => String(v.id) === String(this.idInput && this.idInput.value)) ||
      this.product.variants[0];

    this.addEventListener('change', this.onChange.bind(this));
    this.addEventListener('input', this.queueLimitCheck.bind(this));
    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('submit', this.onSubmit.bind(this));

    this.render();
    this.watchLimit();
  }

  disconnectedCallback() {
    if (this.limitObserver) this.limitObserver.disconnect();
    if (this.onDocumentChange) {
      document.removeEventListener('change', this.onDocumentChange);
      document.removeEventListener('click', this.onDocumentChange);
    }
  }

  onSubmit(event) {
    const form = event.target.closest('form');
    if (!form || !form.classList.contains('pnm-pd__form')) return;

    this.checkLimit();
    if (this.limitExceeded) {
      event.preventDefault();
      return;
    }

    const host = form.closest('product-form');
    if (host && customElements.get('product-form') && typeof host.onSubmitHandler === 'function') return;

    event.preventDefault();
    this.addToCart(form);
  }

  addToCart(form) {
    const button = form.querySelector('[type="submit"]');
    const wrapper = form.parentElement.querySelector('.product-form__error-message-wrapper');
    const message = wrapper && wrapper.querySelector('.product-form__error-message');
    const spinner = button && button.querySelector('.loading__spinner');

    if (wrapper) wrapper.hidden = true;
    if (button) button.setAttribute('aria-disabled', 'true');
    if (spinner) spinner.classList.remove('hidden');

    const drawer = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    const body = new FormData(form);

    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      body.append('sections', drawer.getSectionsToRender().map((section) => section.id).join(','));
      body.append('sections_url', window.location.pathname);
    }

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: body
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status) {
          if (wrapper && message) {
            message.textContent = data.description || data.message || '';
            wrapper.hidden = false;
          }
          return;
        }
        if (drawer && typeof drawer.renderContents === 'function' && data.sections) {
          drawer.classList.remove('is-empty');
          drawer.renderContents(data);
        } else {
          window.location = root + 'cart';
        }
      })
      .catch(() => {
        form.submit();
      })
      .finally(() => {
        if (button) button.removeAttribute('aria-disabled');
        if (spinner) spinner.classList.add('hidden');
        this.syncAtc();
      });
  }

  onClick(event) {
    this.queueLimitCheck();

    const thumb = event.target.closest('[data-pnm-thumb]');
    if (thumb) {
      this.showSlide(Number(thumb.dataset.pnmThumb));
      return;
    }

    const step = event.target.closest('[data-pnm-qty]');
    if (step) {
      const input = this.querySelector('[data-pnm-qty-input]');
      if (!input) return;
      const next = Math.max(1, (parseInt(input.value, 10) || 1) + Number(step.dataset.pnmQty));
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  onChange(event) {
    this.queueLimitCheck();

    if (event.target.matches('[data-pnm-option-input]')) {
      this.resolveVariant();
      this.render();
      return;
    }
    if (event.target.matches('[data-pnm-plan]') || event.target.matches('[data-pnm-purchase]')) {
      this.render();
    }
  }

  buildLimitSelector(raw) {
    const names = String(raw || '')
      .trim()
      .split(/\s+/)
      .map((name) => name.replace(/^\./, ''))
      .filter(Boolean);
    if (!names.length) return '';
    const escape = window.CSS && typeof window.CSS.escape === 'function' ? window.CSS.escape : (value) => value;
    return names.map((name) => '.' + escape(name)).join('');
  }

  get limitScope() {
    return this.closest('.pnm-pd') || this;
  }

  limitGroups() {
    if (!this.limitSelector) return [];
    try {
      const local = this.limitScope.querySelectorAll(this.limitSelector);
      if (local.length) return Array.from(local);
      return Array.from(document.querySelectorAll(this.limitSelector));
    } catch (error) {
      return [];
    }
  }

  countSelections(groups) {
    const seen = new Set();
    let total = 0;

    groups.forEach((group) => {
      group.querySelectorAll('input, select').forEach((field) => {
        if (seen.has(field) || field.disabled) return;
        seen.add(field);

        if (field.tagName === 'SELECT') {
          Array.from(field.selectedOptions).forEach((option) => {
            if (option.value !== '') total += 1;
          });
          return;
        }

        const type = (field.type || '').toLowerCase();
        if (type === 'checkbox' || type === 'radio') {
          if (field.checked) total += 1;
          return;
        }

        if (type === 'number') {
          const amount = parseInt(field.value, 10);
          if (amount > 0) total += amount;
        }
      });
    });

    return total;
  }

  watchLimit() {
    if (!this.limitEnabled || !this.limitSelector) return;

    if (typeof MutationObserver !== 'undefined') {
      this.limitObserver = new MutationObserver(() => this.queueLimitCheck());
      this.limitObserver.observe(this.limitScope, { childList: true, subtree: true });
    }

    this.onDocumentChange = (event) => {
      if (this.limitSelector && event.target.closest && event.target.closest(this.limitSelector)) {
        this.queueLimitCheck();
      }
    };
    document.addEventListener('change', this.onDocumentChange);
    document.addEventListener('click', this.onDocumentChange);

    window.addEventListener('load', () => this.checkLimit());
    this.checkLimit();
  }

  queueLimitCheck() {
    if (!this.limitEnabled || this.limitQueued) return;
    this.limitQueued = true;
    window.requestAnimationFrame(() => {
      this.limitQueued = false;
      this.checkLimit();
    });
  }

  checkLimit() {
    const exceeded = this.limitEnabled && this.countSelections(this.limitGroups()) > this.limitMax;
    if (exceeded === this.limitExceeded) return;
    this.limitExceeded = exceeded;
    this.syncAtc();
  }

  syncAtc() {
    if (!this.atc || !this.variant) return;

    const available = Boolean(this.variant.available);
    const locked = this.limitExceeded;
    const span = this.atc.querySelector('span');

    this.atc.disabled = !available || locked;
    this.atc.dataset.pnmLimit = locked ? 'true' : 'false';

    if (locked) {
      this.atc.setAttribute('aria-disabled', 'true');
      this.limitAria = true;
    } else if (this.limitAria) {
      this.atc.removeAttribute('aria-disabled');
      this.limitAria = false;
    }

    if (!span) return;

    let label = span.textContent;
    if (locked) {
      label = this.atc.dataset.limitMessage || label;
    } else if (available) {
      label = this.atc.dataset.label || label;
    } else {
      label = this.atc.dataset.soldout || label;
    }

    if (span.textContent !== label) span.textContent = label;
  }

  showSlide(index) {
    this.querySelectorAll('[data-pnm-slide]').forEach((slide) => {
      const on = Number(slide.dataset.pnmSlide) === index;
      slide.hidden = !on;
      slide.classList.toggle('is-active', on);
    });
    this.querySelectorAll('[data-pnm-thumb]').forEach((thumb) => {
      const on = Number(thumb.dataset.pnmThumb) === index;
      thumb.classList.toggle('is-active', on);
      thumb.setAttribute('aria-selected', String(on));
    });
  }

  resolveVariant() {
    const selected = Array.from(this.querySelectorAll('[data-pnm-option-input]:checked')).map((input) => input.value);
    if (!selected.length) return;
    const match = this.product.variants.find((v) => selected.every((value, i) => v.options[i] === value));
    if (match) this.variant = match;
  }

  get isSubscribe() {
    const checked = this.querySelector('[data-pnm-purchase]:checked');
    return checked ? checked.value === 'subscribe' : false;
  }

  get allocation() {
    if (!this.planSelect || !this.variant.selling_plan_allocations) return null;
    const id = String(this.planSelect.value);
    return this.variant.selling_plan_allocations.find((a) => String(a.selling_plan_id) === id) || null;
  }

  money(cents) {
    const format = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: format || 'USD'
      }).format(cents / 100);
    } catch (error) {
      return (cents / 100).toFixed(2);
    }
  }

  render() {
    const variant = this.variant;

    if (this.idInput) this.idInput.value = variant.id;

    if (this.priceEl) this.priceEl.textContent = this.money(variant.price);
    if (this.compareEl) {
      const show = variant.compare_at_price && variant.compare_at_price > variant.price;
      this.compareEl.hidden = !show;
      if (show) this.compareEl.textContent = this.money(variant.compare_at_price);
    }
    if (this.oncePrice) this.oncePrice.textContent = this.money(variant.price);

    const allocation = this.allocation;
    if (this.subNow) this.subNow.textContent = allocation ? this.money(allocation.price) : this.money(variant.price);
    if (this.subWas) {
      this.subWas.textContent =
        allocation && allocation.compare_at_price && allocation.compare_at_price > allocation.price
          ? this.money(allocation.compare_at_price)
          : '';
    }

    if (this.planInput) {
      const subscribe = this.isSubscribe && this.planSelect;
      this.planInput.value = subscribe ? this.planSelect.value : '';
      this.planInput.disabled = !subscribe;
    }

    this.querySelectorAll('[data-pnm-option-input]').forEach((input) => {
      const index = Number(input.closest('[data-pnm-option]').dataset.pnmOption);
      const candidate = this.product.variants.find(
        (v) => v.options[index] === input.value && v.options.every((opt, i) => i === index || opt === variant.options[i])
      );
      input.disabled = candidate ? !candidate.available : false;

      const priceEl = input.parentElement.querySelector('[data-pnm-card-price]');
      if (priceEl && candidate) priceEl.textContent = this.money(candidate.price);
    });

    this.syncAtc();

    if (variant.featured_media) {
      const index = this.product.media.findIndex((m) => m.id === variant.featured_media.id);
      if (index > -1) this.showSlide(index);
    }

    if (this.dataset.updateUrl === 'true' && window.history.replaceState) {
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${variant.id}`);
    }
  }
}

if (!customElements.get('pnm-product')) {
  customElements.define('pnm-product', PnmProduct);
}